import { useState, useMemo, useCallback, memo } from 'react';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Icon,
  useColorModeValue,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { CheckCircle2, AlertCircle, Pencil } from 'lucide-react';
import { Issue } from '../../types';
import { ResolveIssueModal } from './ResolveIssueModal';
import { EditIssueForm } from './EditIssueForm';

interface IssuesSectionProps {
  issues: Issue[];
  rockId: string;
  onIssueClick?: (rockId: string, hasOpenIssues?: boolean) => void;
  onResolveIssue?: (issueId: string, resolutionNotes: string, relatedRockId: string) => Promise<void>;
  updateIssue?: (issueId: string, name: string, description: string) => Promise<void>;
  onStatusChange: (status: 'on-track' | 'off-track') => void;
  onRefresh: () => Promise<void>;
  isReadOnly?: boolean;
}

const IssuesSectionComponent = ({
  issues,
  rockId,
  onIssueClick,
  onResolveIssue,
  updateIssue,
  onStatusChange,
  onRefresh,
  isReadOnly = false,
}: IssuesSectionProps) => {
  const [selectedIssueToResolve, setSelectedIssueToResolve] = useState<{ id: string; name: string } | null>(null);
  const [editingIssueId, setEditingIssueId] = useState<string | null>(null);
  
  // Modal for Resolving Issue
  const { isOpen: isResolveModalOpen, onOpen: onResolveModalOpen, onClose: onResolveModalClose } = useDisclosure();
  const toast = useToast();

  const issueBg = useColorModeValue('red.50', 'red.900');
  const issueBorder = useColorModeValue('red.200', 'red.600');
  const issueText = useColorModeValue('red.700', 'red.300');
  const textColorLight = useColorModeValue('gray.500', 'gray.400');
  const textColorLighter = useColorModeValue('gray.600', 'gray.500');

  // Memoize issue arrays - filter open/in-progress vs resolved
  const openIssues = useMemo(() => issues.filter(issue => issue.status === 'open' || issue.status === 'in-progress'), [issues]);
  const resolvedIssues = useMemo(() => issues.filter(issue => issue.status === 'solved'), [issues]);

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString();
    }
    return new Date(timestamp).toLocaleDateString();
  };

  // Handle opening the resolve modal
  const handleResolveClick = useCallback((issue: Issue) => {
    setSelectedIssueToResolve({ id: issue.id, name: issue.name });
    onResolveModalOpen();
  }, [onResolveModalOpen]);

  // Check if the issue being resolved is the last one
  const isLastIssue = useMemo(() => {
    if (!selectedIssueToResolve) return false;
    // openIssues includes the one being resolved
    return openIssues.length === 1 && openIssues[0].id === selectedIssueToResolve.id;
  }, [selectedIssueToResolve, openIssues]);

  // Handle confirming resolution
  const handleConfirmResolve = async (resolutionNotes: string) => {
    if (!selectedIssueToResolve || !onResolveIssue) return;
    
    try {
      await onResolveIssue(selectedIssueToResolve.id, resolutionNotes, rockId);
      await onRefresh();
      
      toast({
        title: 'Issue Resolved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onResolveModalClose();
      setSelectedIssueToResolve(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resolve issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleMarkOnTrack = async (resolutionNotes: string) => {
    if (!selectedIssueToResolve || !onResolveIssue) return;
    
    try {
      // First resolve the issue
      await onResolveIssue(selectedIssueToResolve.id, resolutionNotes, rockId);
      // Then mark as on-track
      onStatusChange('on-track');

      await onRefresh();
      
      toast({
        title: 'Issue Resolved & Rock On Track',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onResolveModalClose();
      setSelectedIssueToResolve(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resolve issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleIssueUpdated = useCallback(async () => {
    await onRefresh();
    setEditingIssueId(null);
  }, [onRefresh]);

  return (
    <Box bg={issueBg} border="1px" borderColor={issueBorder} borderRadius="lg" p={4}>
      <Flex justify="space-between" align="center" mb={2}>
        <HStack spacing={2}>
          <Icon as={AlertCircle} w={4} h={4} color="red.500" />
          <Text fontWeight="medium" fontSize="sm" color={issueText}>
            Issues ({openIssues.length} open)
          </Text>
        </HStack>
        {onIssueClick && (
          <Button
            size="xs"
            colorScheme="red"
            onClick={() => onIssueClick(rockId, openIssues.length > 0)}
            isDisabled={isReadOnly}
          >
            Add Issue
          </Button>
        )}
      </Flex>
      {openIssues.length > 0 ? (
        <VStack align="stretch" spacing={2}>
          {openIssues.map((issue) => (
            <Box key={issue.id}>
              {editingIssueId === issue.id ? (
                <EditIssueForm
                  issue={issue}
                  rockId={rockId}
                  onUpdate={updateIssue!}
                  onSuccess={handleIssueUpdated}
                  onCancel={() => setEditingIssueId(null)}
                />
              ) : (
                <HStack spacing={2}>
                  {onResolveIssue && (
                    <Checkbox
                      isChecked={false}
                      onChange={() => handleResolveClick(issue)}
                      colorScheme="green"
                      isDisabled={!onResolveIssue || isReadOnly}
                    />
                  )}
                  {updateIssue && !isReadOnly && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setEditingIssueId(issue.id)}
                      aria-label="Edit issue"
                    >
                      <Icon as={Pencil} w={3} h={3} />
                    </Button>
                  )}
                  <Text
                    fontSize="sm"
                    color={issueText}
                    flex={1}
                  >
                    {issue.name}
                  </Text>
                  {issue.description && (
                    <Text fontSize="xs" color={textColorLight} noOfLines={1} maxW="200px">
                      {issue.description}
                    </Text>
                  )}
                </HStack>
              )}
            </Box>
          ))}
        </VStack>
      ) : (
        <Text fontSize="sm" color={textColorLighter}>No open issues for this rock.</Text>
      )}
      {resolvedIssues.length > 0 && (
        <Accordion allowToggle size="sm" mt={openIssues.length > 0 ? 4 : 2}>
          <AccordionItem border="none">
            <h2>
              <AccordionButton _expanded={{ bg: 'red.100' }} borderRadius="md" px={2} py={1}>
                <Box flex="1" textAlign="left" fontSize="sm" fontWeight="medium" color={issueText}>
                  Completed ({resolvedIssues.length})
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              <VStack align="stretch" spacing={2} mt={2}>
                {resolvedIssues.map((issue) => (
                  <HStack key={issue.id} spacing={2} align="start" opacity={0.6}>
                    <Icon as={CheckCircle2} w={4} h={4} color="green.500" mt={0.5} flexShrink={0} />
                    <VStack align="stretch" spacing={1} flex={1}>
                      <Text
                        fontSize="sm"
                        textDecoration="line-through"
                        color={textColorLight}
                      >
                        {issue.name}
                      </Text>
                      {issue.description && (
                        <Text fontSize="xs" color={textColorLight} noOfLines={2}>
                          {issue.description}
                        </Text>
                      )}
                      {issue.resolutionNotes && (
                        <Text fontSize="xs" color={textColorLight} fontStyle="italic" mt={1}>
                          Resolution: {issue.resolutionNotes}
                        </Text>
                      )}
                      {issue.solvedAt && (
                        <Text fontSize="xs" color={textColorLight}>
                          Resolved: {formatTimestamp(issue.solvedAt)}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                ))}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}

      {selectedIssueToResolve && (
        <ResolveIssueModal
          isOpen={isResolveModalOpen}
          onClose={() => {
            onResolveModalClose();
            setSelectedIssueToResolve(null);
          }}
          onConfirm={handleConfirmResolve}
          issueTitle={selectedIssueToResolve.name}
          isLastIssue={isLastIssue}
          onMarkOnTrack={handleMarkOnTrack}
        />
      )}
    </Box>
  );
};

export const IssuesSection = memo(IssuesSectionComponent);
